import I18n from 'i18n-js';
import {legacyRouteOf, writeEndpointOf} from './sectionConfig';

// Mirrors groovepacker Tenants::Helper#update_subscription_plan, which before GROOV-5248 reprices the Stripe
// subscription to this formula on any PUT /plan_limits whenever it differs from the stored amount. Later backends
// reprice only for max_users and max_administrative_users, so the dialog can over-warn there.
const REGULAR_USER_PRICE = 50;
const ADMIN_USER_PRICE = 10;
const USER_LIMIT_KEYS = ['regularUsers', 'adminOnlyUsers'];
const BLANK_SUBSCRIPTION_IDS = ['', 'null', 'undefined'];

const text = (key, options) => I18n.t(`adminTenantPage.${key}`, options);

export const money = amount => `$${amount}`;

const hasSubscriptionId = subscription =>
  Boolean(
    subscription.customer_subscription_id &&
      !BLANK_SUBSCRIPTION_IDS.includes(subscription.customer_subscription_id)
  );

const subscriptionOf = show =>
  (show && show.tenant && show.tenant.subscription_info) || null;

export const addonPriceOf = (show, feature) =>
  (show &&
    show.tenant &&
    show.tenant.basicinfo &&
    show.tenant.basicinfo.price &&
    show.tenant.basicinfo.price[feature]) ||
  {};

export const subscriptionCents = show => {
  const subscription = subscriptionOf(show);
  if (!subscription) return null;
  const amount = Number(subscription.amount || 0);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
};

export const repricesOnUserLimitsOnly = subscription =>
  Boolean(subscription && subscription.reprices_on_user_limits_only === true);

export const billsThroughStripe = show => {
  const subscription = subscriptionOf(show);
  return Boolean(
    subscription &&
      !subscription.shopify_customer &&
      hasSubscriptionId(subscription)
  );
};

const addonImpact = (feature, value, show) => {
  const before = addonPriceOf(show, feature);
  const subscription = subscriptionOf(show);
  return {
    kind: 'addon',
    feature,
    stripe: billsThroughStripe(show),
    inPlaceUpdate: repricesOnUserLimitsOnly(subscription),
    interval: subscription ? subscription.interval : null,
    before: {toggle: Boolean(before.toggle), amount: before.amount},
    after: {
      toggle: Boolean(value && value.toggle),
      amount: value && value.amount
    }
  };
};

const limitOrCurrent = (settingKey, rowKey, value, current) =>
  Number(settingKey === rowKey ? value : current) || 0;

const planLimitsImpact = (settingKey, value, settings, show) => {
  const subscription = subscriptionOf(show);
  if (!subscription) return null;
  if (
    repricesOnUserLimitsOnly(subscription) &&
    !USER_LIMIT_KEYS.includes(settingKey)
  )
    return null;
  if (subscription.shopify_customer) return {kind: 'shopify'};
  if (!hasSubscriptionId(subscription)) return null;
  const limits = (settings && settings.plan_limits) || {};
  const users = limitOrCurrent(
    settingKey,
    'regularUsers',
    value,
    limits.num_users
  );
  const admins = limitOrCurrent(
    settingKey,
    'adminOnlyUsers',
    value,
    limits.administrative_users
  );
  const to = users * REGULAR_USER_PRICE + admins * ADMIN_USER_PRICE;
  const fromCents = subscriptionCents(show);
  if (fromCents === to * 100) return null;
  return {
    kind: 'reprice',
    from: (fromCents || 0) / 100,
    to,
    interval: subscription.interval,
    reportsOutcome: repricesOnUserLimitsOnly(subscription)
  };
};

export const billingImpactOf = (settingKey, value, {settings, show}) => {
  const legacy = legacyRouteOf(settingKey);
  if (legacy && legacy.kind === 'price')
    return addonImpact(legacy.feature, value, show);
  if (writeEndpointOf(settingKey) === 'plan_limits')
    return planLimitsImpact(settingKey, value, settings, show);
  return null;
};

export const perInterval = interval => {
  if (interval === 'month') return text('billingPerMonth');
  if (interval === 'year') return text('billingPerYear');
  return '';
};

const stripeAddonLines = (impact, tenant, addon) => {
  const {before, after} = impact;
  const per = perInterval(impact.interval);
  if (after.toggle && !before.toggle) {
    return [
      text('billingAddonOn', {addon, tenant, amount: money(after.amount), per}),
      text('billingChargeAdded', {tenant, amount: money(after.amount), per})
    ];
  }
  if (!after.toggle && before.toggle) {
    return [
      text('billingAddonOff', {
        addon,
        tenant,
        amount: money(before.amount),
        per
      }),
      text('billingChargeRemoved', {tenant})
    ];
  }
  const change = text('billingAddonAmount', {
    addon,
    tenant,
    from: money(before.amount),
    to: money(after.amount)
  });
  if (!after.toggle) return [change, text('billingNoCharge', {tenant})];
  if (impact.inPlaceUpdate)
    return [
      change,
      text('billingChargeChanged', {
        tenant,
        from: money(before.amount),
        to: money(after.amount),
        per
      })
    ];
  return [
    change,
    text('billingDoubleCharge', {
      tenant,
      from: money(before.amount),
      to: money(after.amount),
      per
    })
  ];
};

const addonLines = (impact, tenant, addon) => {
  const lines = stripeAddonLines(impact, tenant, addon);
  if (impact.stripe) return lines;
  return [lines[0], text('billingAddonNoStripe', {tenant})];
};

export const billingDialogMessage = (impact, tenant, settingLabel) => {
  if (impact.kind === 'addon')
    return addonLines(impact, tenant, settingLabel).join('\n\n');
  if (impact.kind === 'shopify')
    return text('billingShopifyEmail', {tenant, setting: settingLabel});
  return text('billingReprice', {
    tenant,
    setting: settingLabel,
    from: money(impact.from.toFixed(2)),
    to: money(impact.to.toFixed(2)),
    per: perInterval(impact.interval)
  });
};

export const isDoubleCharge = impact =>
  impact.kind === 'addon' &&
  impact.stripe &&
  !impact.inPlaceUpdate &&
  impact.before.toggle &&
  impact.after.toggle;

const BILLING_NOTICES = {
  notApplied: 'billingNotApplied',
  notConfirmed: 'billingNotConfirmed'
};

const withDetail = (message, result) =>
  result.billingError
    ? `${message} ${text('billingErrorDetail', {error: result.billingError})}`
    : message;

export const billingNoticeOf = (result, tenant) => {
  if (result.billing === 'recorded')
    return {
      tone: 'error',
      message: text('billingRepriceRecorded', {
        tenant,
        amount: money(result.repricedTo.amount.toFixed(2)),
        per: perInterval(result.repricedTo.interval)
      })
    };
  if (result.billing === 'notApplied' && result.billingKind === 'reprice')
    return {
      tone: 'error',
      message: withDetail(text('billingRepriceNotApplied', {tenant}), result)
    };
  const key = BILLING_NOTICES[result.billing];
  return key
    ? {tone: 'error', message: withDetail(text(key, {tenant}), result)}
    : null;
};
